// Lightweight image readiness utility.
//
// Returns a Promise that resolves when the image is fully fetched AND
// decoded (where supported), or when a safety timeout elapses. The
// returned Promise never rejects — callers can treat resolution as
// "safe to proceed" without try/catch noise. If the image fails to
// load or decode, the Promise still resolves after the timeout so the
// UI is never permanently gated by a broken/unreachable asset.
//
// Usage:
//   await preloadImage(src);            // up to 4500ms wait
//   await preloadImage(src, 6000);      // custom timeout

const DEFAULT_TIMEOUT_MS = 4500;

export function preloadImage(src, timeoutMs = DEFAULT_TIMEOUT_MS) {
  if (!src || typeof window === "undefined") return Promise.resolve();

  return new Promise((resolve) => {
    let settled = false;
    const done = () => {
      if (settled) return;
      settled = true;
      resolve();
    };

    const safety = window.setTimeout(done, timeoutMs);

    const img = new Image();
    img.decoding = "async";

    const onReady = () => {
      window.clearTimeout(safety);
      // Prefer image.decode() when available — it forces the image to
      // be decoded off the main thread so the first paint can use it
      // without a flash.
      if (typeof img.decode === "function") {
        img.decode().then(done, done);
      } else {
        done();
      }
    };

    img.onload = onReady;
    img.onerror = () => {
      // Let the safety timeout resolve naturally so a flaky CDN
      // doesn't keep the UI stuck — but kick it sooner since we
      // already know the network result.
      window.clearTimeout(safety);
      window.setTimeout(done, 200);
    };

    img.src = src;
  });
}

// Quietly prefetch a list of image sources in the background. Uses
// requestIdleCallback when available so it never competes with the
// hero animation or user interaction. Best-effort: failures are
// silently ignored.
export function idlePrefetchImages(sources) {
  if (!sources || !sources.length || typeof window === "undefined") return;

  const run = () => {
    sources.forEach((src) => {
      if (!src) return;
      const img = new Image();
      img.decoding = "async";
      img.src = src;
    });
  };

  if (typeof window.requestIdleCallback === "function") {
    window.requestIdleCallback(run, { timeout: 2000 });
  } else {
    window.setTimeout(run, 800);
  }
}
